import { EmptyState } from '@/components/page-header';
import { PurchaseForm } from '@/components/purchases/purchase-form';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { usePurchaseDocument, type PurchaseKind } from '@/lib/purchases';
import { useCompanySettings } from '@/lib/settings';

/** Route body for "new" and "view/edit" of a bill, expense or debit note. */
export function PurchasePage({
  kind,
  id,
  copyOf,
  contactId,
  openApply,
  fromInbox,
  fromPurchaseOrder,
}: {
  kind: PurchaseKind;
  id?: string;
  copyOf?: string;
  contactId?: string;
  openApply?: boolean;
  fromInbox?: string;
  /** New bill: receive this purchase order's open lines (Copy to bill). */
  fromPurchaseOrder?: string;
}) {
  const t = useT();
  const orgId = useOrgId();
  const doc = usePurchaseDocument(id);
  const source = usePurchaseDocument(copyOf);
  const receive = usePurchaseDocument(fromPurchaseOrder);
  const settings = useCompanySettings();
  if (!orgId)
    return (
      <EmptyState title={t('shell.selectClient')} hint={t('purchasePages.page.selectClientHint')} />
    );
  if (
    (id && doc.isPending) ||
    (copyOf && source.isPending) ||
    (fromPurchaseOrder && receive.isPending) ||
    settings.isPending
  )
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (id && doc.isError)
    return <EmptyState title={t('purchasePages.page.notFound')} hint={doc.error.message} />;
  if (id && doc.data && doc.data.kind !== kind)
    return (
      <EmptyState
        title={t('purchasePages.page.notFound')}
        hint={t('purchasePages.page.wrongKind')}
      />
    );
  return (
    <PurchaseForm
      key={`${orgId}-${id ?? 'new'}-${doc.data?.version ?? 0}-${copyOf ?? ''}-${fromPurchaseOrder ?? ''}`}
      kind={kind}
      doc={doc.data ?? null}
      source={source.data ?? null}
      {...(contactId ? { defaultContactId: contactId } : {})}
      {...(openApply ? { openApply } : {})}
      {...(fromInbox ? { fromInbox } : {})}
      {...(receive.data ? { receive: receive.data } : {})}
    />
  );
}

export type NewPurchaseSearch = {
  copyOf?: string;
  contactId?: string;
  fromInbox?: string;
  fromPurchaseOrder?: string;
};

export function validateNewPurchaseSearch(s: Record<string, unknown>): NewPurchaseSearch {
  const out: NewPurchaseSearch = {};
  for (const k of ['copyOf', 'contactId', 'fromInbox', 'fromPurchaseOrder'] as const)
    if (typeof s[k] === 'string') out[k] = s[k] as string;
  return out;
}
